/**
 * FauxDS FilterGroup - Recreated from Figma MCP
 *
 * ⚠️ This is a SIMPLIFIED RECREATION of the Slate FilterGroup component for prototyping.
 * It is NOT the real @faire/slate FilterGroup and should NOT be used for:
 * - Engineering handoff
 * - Design specs
 * - Production code
 *
 * For the real Slate FilterGroup, see: @faire/slate/components/filter
 *
 * This component was recreated from Figma MCP design specs:
 * - Figma node: 48781:39660 (Slate Filter Group)
 * - Design tokens extracted from Figma variables
 * - All variants verified: Amount 2-10 filter toggles
 * - Optional FilterButton support verified
 * - Spacing and layout verified
 */
import { cn } from "@faire/prototype-playground/lib/utils";
import React, { forwardRef } from "react";

import FilterButton from "./FilterButton";
import FilterToggle from "./FilterToggle";

export interface FilterGroupItem {
  /**
   * Label text for the filter toggle
   */
  label: string;
  /**
   * Whether this filter toggle is applied
   * @default false
   */
  applied?: boolean;
  /**
   * Custom icon component (ReactNode)
   */
  Icon?: React.FC<any>;
  /**
   * Accessibility label
   */
  "aria-label"?: string;
  /**
   * Click handler for this toggle
   */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

export interface FilterGroupProps {
  /**
   * Accessibility label for the entire group
   * @default "Label"
   */
  "aria-label"?: string;
  /**
   * Custom items array (overrides default items based on amount)
   */
  items: FilterGroupItem[];
  /**
   * FilterButton props (when filterButton is true)
   */
  filterButtonProps?: {
    label: string;
    Icon?: React.FC<any>;
    onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
    "aria-label"?: string;
  };

  /**
   * **playground only**
   * Optional className for additional styling
   */
  className?: string;
}

/**
 * FilterGroup - FauxDS (recreated from Figma MCP)
 *
 * A Filter Group is a grouped component with defined spacing and overflow behavior.
 * It is a combination of 1 Filter Button and as many Filter Toggles as needed.
 *
 * Usage tips:
 * - Use a Filter Group to quickly build a toolbar of grouped Filter Toggles
 * - The optional Filter Button component can be used to open a larger set of filtering options
 * - Consider the screen size. Filter Groups can either be scrollable when their content
 *   extends beyond the viewport or wrap, extending to additional vertical lines
 *
 * This implementation was created by extracting design specs directly from Figma.
 */
const FilterGroup = forwardRef<HTMLDivElement, FilterGroupProps>(
  (
    {
      "aria-label": accessibilityLabel = "Label",
      items,
      filterButtonProps,
      className,
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={cn(
          "flex flex-wrap items-center gap-(--slate-dimensions-2)",
          className
        )}
        role="group"
        aria-label={accessibilityLabel}
      >
        {/* FilterButton (optional) */}
        {filterButtonProps && (
          <FilterButton
            variant="rest"
            label={filterButtonProps?.label}
            Icon={filterButtonProps?.Icon}
            onClick={filterButtonProps?.onClick}
            aria-label={filterButtonProps?.["aria-label"]}
          />
        )}

        {/* FilterToggles */}
        {items.map((item, index) => (
          <FilterToggle
            key={index}
            label={item.label}
            applied={item.applied || false}
            Icon={item.Icon}
            onClick={item.onClick}
            aria-label={item["aria-label"]}
          />
        ))}
      </div>
    );
  }
);

FilterGroup.displayName = "FilterGroup";

export default FilterGroup;
